import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./ui/button";
import { MaskLines } from "./Reveal";
import { IMAGES } from "../lib/content";
import { MapPin, ArrowDown } from "lucide-react";

export const Hero = ({ onBook, onPriority }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen pt-28 pb-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white/60 px-4 py-1.5 text-xs font-semibold tracking-wide text-brand-ink/70 mb-8"
          >
            <MapPin size={14} className="text-brand" />
            NOW SERVING JAIPUR
          </motion.div>

          <h1 className="font-serif font-light text-brand-ink leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            <MaskLines
              delay={0.15}
              lines={["We look after", "your parents"]}
            />
            <span className="line-mask">
              <motion.span
                className="block italic text-brand"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                when you can't be there.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-brand-ink/70 leading-relaxed"
          >
            For elders living alone in Jaipur while their children are away —
            weekly visits, health check-ups, medicines and a live call the
            moment we arrive. All with your permission.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={onBook}
              className="rounded-full bg-brand hover:bg-brand-hover text-white px-8 h-14 text-base transition-transform active:scale-95"
            >
              Request a home visit
            </Button>
            <Button
              data-testid="hero-priority-btn"
              onClick={onPriority}
              variant="outline"
              className="rounded-full border-brand text-brand hover:bg-brand/5 px-8 h-14 text-base transition-transform active:scale-95"
            >
              Prioritise a doctor appointment
            </Button>
            <a
              href="#contact"
              data-testid="hero-cremation-btn"
              className="inline-flex items-center justify-center rounded-full border border-brand-ink/30 text-brand-ink hover:bg-brand-ink hover:text-white px-8 h-14 text-base transition-colors"
            >
              Cremation &amp; last rites support
            </a>
            <a
              href="#services"
              data-testid="hero-explore-link"
              className="inline-flex items-center gap-2 text-base font-medium text-brand-ink hover:text-brand transition-colors"
            >
              <span className="border-b border-brand-ink/30 pb-0.5">
                Explore our care
              </span>
              <ArrowDown size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-14 flex items-center gap-8 text-sm text-brand-ink/60"
          >
            <div>
              <div className="font-serif text-3xl text-brand-ink">500+</div>
              families supported
            </div>
            <div className="h-10 w-px bg-border" />
            <div>
              <div className="font-serif text-3xl text-brand-ink">24/7</div>
              emergency response
            </div>
          </motion.div>
        </div>

        {/* Right image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-xl"
          >
            <motion.img
              src={IMAGES.heroPortrait}
              alt="A smiling senior woman"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-full object-cover"
            />
            <div className="grain" />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/85 backdrop-blur-md px-5 py-4">
              <p className="text-sm font-medium text-brand-ink">
                “They call me the moment they reach Maa's door.”
              </p>
              <p className="text-xs text-brand-ink/60 mt-1">— Rohan, Toronto</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
